import type { InvoiceListItemDTO } from '@kanbu/schema/contracts';
import { Trans } from '@lingui/react/macro';
import { Button, Dialog, Input, Label } from '@utima/ui';
import { Info, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

type ReissueInvoiceDialogProps = {
  invoice: InvoiceListItemDTO | null;
  isOpen: boolean;
  isPending: boolean;
  onClose: () => void;
  onConfirm: (duzpDate: string) => void;
};

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function ReissueInvoiceDialog({
  invoice,
  isOpen,
  isPending,
  onClose,
  onConfirm,
}: ReissueInvoiceDialogProps) {
  const [duzpDate, setDuzpDate] = useState<string>(todayIso());

  // Default the DUZP to the original invoice's tax point each time the dialog
  // opens for a different invoice (falls back to today when absent).
  useEffect(() => {
    if (isOpen) {
      setDuzpDate(invoice?.duzpDate ?? todayIso());
    }
  }, [isOpen, invoice?.duzpDate]);

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={open => {
        if (!open && !isPending) {
          onClose();
        }
      }}
    >
      <Dialog.Content>
        <Dialog.Header>
          <Dialog.Title>
            <Trans>Void & reissue invoice</Trans>
          </Dialog.Title>
          <Dialog.Description>
            <Trans>
              Voids the original invoice and issues a corrected copy with the
              same line items, amounts and tax. Use this to fix the billing
              identity (IČO / address / VAT) or the DUZP — the customer can no
              longer pay the original.
            </Trans>
          </Dialog.Description>
        </Dialog.Header>

        <div className='space-y-4 py-2'>
          <div className='text-muted-foreground flex items-start gap-2 rounded-md bg-muted/50 p-3 text-xs'>
            <Info className='mt-0.5 size-3.5 shrink-0' />
            <span>
              <Trans>
                To correct billing details, edit the organization billing info
                first — the reissue picks up the current Stripe customer data
                automatically. To change the plan or price, use{' '}
                <strong>Assign / replace plan</strong> instead; reissue keeps
                the same line items.
              </Trans>
            </span>
          </div>

          <div className='space-y-1.5'>
            <Label htmlFor='reissue-duzp'>
              <Trans>DUZP (tax point)</Trans>
            </Label>
            <Input
              id='reissue-duzp'
              type='date'
              value={duzpDate}
              onChange={event => setDuzpDate(event.target.value)}
            />
            <p className='text-muted-foreground text-xs'>
              <Trans>Defaults to the DUZP from the original invoice.</Trans>
            </p>
          </div>
        </div>

        <Dialog.Footer>
          <Button variant='secondary' onClick={onClose} disabled={isPending}>
            <Trans>Cancel</Trans>
          </Button>
          <Button
            onClick={() => onConfirm(duzpDate)}
            disabled={isPending || !duzpDate}
          >
            {isPending && <Loader2 className='mr-2 size-4 animate-spin' />}
            <Trans>Void & reissue</Trans>
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
}
